import { useEffect, useState } from "react";

import { TextField, Button, Checkbox } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models/Common/CodeBook";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { searchRequestMetadata } from "../../api/Request/RequestApi";
import {
  getSchoolCandidateById,
  saveSchoolCandidateInstitutionCountyAssignment,
  saveSchoolCandidateRemark,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import {
  AddressSelect,
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { GenderEnum, Role, SaveResult, ValidationFailure } from "../../models";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { RequestMetadata, RequestMetadataSearch } from "../../models/Request";
import {
  SchoolCandidate,
  UpdateSchoolCandidateInstitutionCountyAssignment,
  SchoolCandidateInstitutionChangeType,
  SchoolCandidateStatus,
  getDefaultSchoolCandidate,
  SchoolCandidateRemarkType,
} from "../../models/SchoolCandidate";
import { ClaimType } from "../../models/User";
import {
  useGetAlternativeCodeReasonListQuery,
  useGetAlternativeCodeTypeListQuery,
  useGetCountyListForUserQuery,
  useGetInstitutionListForUserQuery,
  useGetSchoolCandidateAddReasonListQuery,
  useGetSchoolCandidateCancelReasonListQuery,
} from "../../queries/codeBookQuery";
import { useGetRemarksQuery } from "../../queries/remarkQuery";
import {
  schoolCandidateEditUrl,
  schoolCandidateInstitutionAdminEditUrl,
  schoolCandidateInstitutionAdminListRoute,
  schoolCandidateInstitutionReassignmentUrl,
} from "../../routes/routes";
import { resetRedirectURL } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";
import { ConfirmationModal } from "./components";
import { getSchoolCandidateNotesUrl, isDetailsEditVisible } from "./utils";

export function SchoolCandidateDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const redirectURL = useTypedSelector(
    (state) => state.application.redirectURL
  );

  const defaultSchoolCandidate = getDefaultSchoolCandidate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [schoolCandidate, setSchoolCandidate] = useState<SchoolCandidate>(
    defaultSchoolCandidate
  );
  const [genders] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(GenderEnum, "gender")
  );
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(SchoolCandidateStatus, "schoolCandidateStatus")
  );
  //const { data: genders } = useGetGenderList();
  const { data: counties } = useGetCountyListForUserQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const { data: schoolCandidateAlternativeCodeReasons } =
    useGetAlternativeCodeReasonListQuery();
  const { data: schoolCandidateAlternativeCodeTypes } =
    useGetAlternativeCodeTypeListQuery();
  const { data: schoolCandidateAddReasons } =
    useGetSchoolCandidateAddReasonListQuery();
  const { data: schoolCandidateCancelReasons } =
    useGetSchoolCandidateCancelReasonListQuery();
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [requests, setRequests] = useState<RequestMetadata[] | undefined>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { data: remarks = [] } = useGetRemarksQuery(+id);

  useEffect(() => {
    (async () => {
      var result = await getSchoolCandidateById(+id);
      if (result) {
        setSchoolCandidate(result);
        if (result.schoolYear && result.schoolYear.isCurrent) {
          var eventId = ImportantDateEventEnum.DeliveryOfInitialLists as number;
          var importantDate = result.schoolYear.importantDates.find(
            (val) => val.eventId === eventId
          );
          if (importantDate) {
            var exactlyNow = new Date(Date.now());
            var dateFrom = new Date(importantDate.dateTimeFrom);
            var dateTo = new Date(importantDate.dateTimeTo);
            if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
              setIsEditable(true);
            }
          }
        }
        setIsLoading(false);
      }
      const requestSearch: RequestMetadataSearch = {
        institutionIds: [],
        schoolCandidateId: +id,
      };
      var requests = await searchRequestMetadata(requestSearch);
      if (requests && requests.results) setRequests(requests.results);
    })();
  }, [id]);
  const handleEditClicked = () => {
    const adminUrl = schoolCandidateInstitutionAdminListRoute;
    if (history.location.pathname.startsWith(adminUrl))
      history.push(schoolCandidateInstitutionAdminEditUrl(+id));
    else history.push(schoolCandidateEditUrl(+id));
  };
  const handleReassignmentClicked = () => {
    history.push(schoolCandidateInstitutionReassignmentUrl(+id));
  };
  const handleConfirmationRequiredClicked = () => {
    setShowConfirmationModal(true);
  };
  const handleConfirmationSubmit = async (note: string) => {
    const newUpdate: UpdateSchoolCandidateInstitutionCountyAssignment = {
      schoolCandidateId: +id,
      changeType: SchoolCandidateInstitutionChangeType.Confirmation,
    };

    const result = await saveSchoolCandidateInstitutionCountyAssignment(
      newUpdate
    );

    let resultRemark: SaveResult<string> | null = null;

    if (note) {
      resultRemark = await saveSchoolCandidateRemark(
        +id,
        SchoolCandidateRemarkType.CountyVerificationRemark,
        note
      );
    }

    if (result?.isValid && (!resultRemark || resultRemark?.isValid)) {
      history.goBack();
    } else {
      setErrors(result.validationFailures);
    }
  };

  const goBack = () => {
    if (redirectURL) {
      dispatch(resetRedirectURL());
      history.push(redirectURL);
    } else history.goBack();
  };
  if (isLoading) return null;

  const isAllowedToEdit =
    isEditable &&
    !(requests && requests.length > 0) &&
    isDetailsEditVisible(auth);

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.schoolCandidateModel.schoolCandidateDetails")}
        showBack={"show"}
        showEdit={isAllowedToEdit ? "show" : "disable"}
        onClickBack={goBack}
        onClickEdit={handleEditClicked}
        errors={errors}
      />

      <FormCard
        cardTitle={t(
          "common.schoolCandidateModel.schoolCandidatePersonalDetails"
        )}
        labelStatus={{
          color: FormatUtils.getSchoolCandidateStatusColor(
            schoolCandidate.statusId
          ),
          name:
            statuses.find((v) => v.id === schoolCandidate.statusId)?.name ?? "",
        }}
      >
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              value={schoolCandidate.oib}
              readonly
              plaintext
            />
          </Col>
          <Col md={2}></Col>
          <Col md={6}></Col>
        </Row>
        {schoolCandidate.alternativeCode &&
          schoolCandidate.schoolCandidateAlternativeCodeTypeId &&
          schoolCandidate.schoolCandidateAlternativeCodeReasonId && (
            <Row>
              <Col md={4}>
                <TextField
                  label={t("common.schoolCandidateModel.alternativeCode")}
                  value={schoolCandidate.alternativeCode}
                  readonly
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  label={t(
                    "common.schoolCandidateModel.schoolCandidateAlternativeCodeType"
                  )}
                  value={
                    schoolCandidateAlternativeCodeTypes?.find(
                      (v) =>
                        v.id ===
                        schoolCandidate.schoolCandidateAlternativeCodeTypeId
                    )?.name
                  }
                  readonly
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  label={t(
                    "common.schoolCandidateModel.schoolCandidateAlternativeCodeReason"
                  )}
                  value={
                    schoolCandidateAlternativeCodeReasons?.find(
                      (v) =>
                        v.id ===
                        schoolCandidate.schoolCandidateAlternativeCodeReasonId
                    )?.name
                  }
                  readonly
                  plaintext
                />
              </Col>
            </Row>
          )}
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              value={schoolCandidate.lastName}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              value={schoolCandidate.firstName}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.dateOfBirth")}
              value={hrDate(schoolCandidate.dateOfBirth)}
              readonly
              plaintext
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              value={
                genders.find((v) => v.id === schoolCandidate.genderId)?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.fatherName")}
              value={schoolCandidate.fatherName}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.motherName")}
              value={schoolCandidate.motherName}
              readonly
              plaintext
            />
          </Col>
        </Row>
      </FormCard>

      <AddressSelect
        title={t("common.schoolCandidateModel.permanentAddress")}
        initialValue={schoolCandidate.residence?.permanentAddress}
        readOnly={true}
      >
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.permanentAddressNote")}
              value={schoolCandidate.permanentAddressNote}
              readonly
              plaintext
            />
          </Col>
        </Row>
      </AddressSelect>

      <AddressSelect
        title={t("common.schoolCandidateModel.temporaryAddress")}
        initialValue={schoolCandidate.residence?.temporaryAddress}
        readOnly={true}
      ></AddressSelect>

      <FormCard cardTitle={t("common.schoolCandidateModel.otherDetails")}>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateAddReason")}
              value={
                schoolCandidateAddReasons?.find(
                  (v) => v.id === schoolCandidate.schoolCandidateAddReasonId
                )?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateAddReasonOther"
              )}
              value={schoolCandidate.schoolCandidateAddReasonOther}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateCancelReason"
              )}
              value={
                schoolCandidateCancelReasons?.find(
                  (v) => v.id === schoolCandidate.schoolCandidateCancelReasonId
                )?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateCancelReasonOther"
              )}
              value={schoolCandidate.schoolCandidateCancelReasonOther}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.uoguCounty")}
              value={
                counties?.find((v) => v.id === schoolCandidate.uoguCountyId)
                  ?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.institutionList")}
              value={
                institutions?.find(
                  (v) => v.id === schoolCandidate.institutionId
                )?.name
              }
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.isHandicapped")}
              checked={schoolCandidate.isHandicapped}
              plaintext={true}
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showCancel="hide"
        showSave="hide"
        showCustom={true}
        hasSchoolAttendanceNote={{
          visible: true,
          redirectUrl: getSchoolCandidateNotesUrl(window.location.href, +id),
          outline: !!remarks.length,
        }}
      >
        {(auth.currentRole?.role === Role.CAS ||
          auth.currentRole?.role === Role.Helpdesk ||
          auth.currentRole?.claimType === ClaimType.County) && (
          <Button
            outline={true}
            onClick={handleReassignmentClicked}
            disabled={
              schoolCandidate.statusId !== SchoolCandidateStatus.confirmation ||
              !isEditable
            }
          >
            {t("common.schoolCandidateModel.reassignInstitution")}
          </Button>
        )}
        {(auth.currentRole?.role === Role.CAS ||
          auth.currentRole?.role === Role.Helpdesk ||
          auth.currentRole?.claimType === ClaimType.County ||
          auth.currentRole?.claimType === ClaimType.Institution) && (
          <Button
            outline={true}
            onClick={handleConfirmationRequiredClicked}
            disabled={
              schoolCandidate.statusId !== SchoolCandidateStatus.onSchoolList ||
              !isEditable
            }
          >
            {t("common.schoolCandidateModel.confirmationRequired")}
          </Button>
        )}
      </ToolbarEdit>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmationSubmit}
      />
    </ContentArea>
  );
}
